// Browser history for the booking steps (Frontend §4.7; UserFlows §2.4 "Navigation and refresh"), ported from the
// mockup's `BookingFlow` `go` / `back` / `popstate` logic behind one interface. Direct link: real history entries
// (`pushState({ booking, idx, step })`, same URL). Embed (task 08): an internal stack, the steps' own Back only.
import type { BookingStep } from './machine'

export interface StepHistory {
  /** The first booking step (P-07, or P-08 when P-07 is skipped): replaces the current entry. */
  start(step: BookingStep): void
  /** A new entry (P-07 → P-08 → P-09). */
  push(step: BookingStep): void
  /** The current entry becomes `step` (P-09 → P-10, P-10 → P-11 / P-12, a corrected landing). */
  replace(step: BookingStep): void
  /** Back to the latest earlier `to` (`history.go(-n)`); the pop listener then sees it. */
  back(to: BookingStep): void
  /** Browser Back / Forward landed on a step of this booking. Returns the unsubscribe. */
  onPop(cb: (target: BookingStep) => void): () => void
}

interface Entry {
  booking: string
  idx: number
  step: BookingStep
}

const isEntry = (v: unknown, booking: string): v is Entry =>
  typeof v === 'object' && v !== null && (v as Partial<Entry>).booking === booking

/** Direct link: one history entry per booking step, all on the same URL (no patient data in history state). */
export function createBrowserStepHistory(sessionId: string, h: History = window.history): StepHistory {
  let idx = 0
  let stack: BookingStep[] = []
  const write = (method: 'pushState' | 'replaceState', step: BookingStep) => {
    h[method]({ booking: sessionId, idx, step } satisfies Entry, '')
  }
  return {
    start(step) {
      idx = 0
      stack = [step]
      write('replaceState', step)
    },
    push(step) {
      stack = [...stack.slice(0, idx + 1), step]
      idx += 1
      write('pushState', step)
    },
    replace(step) {
      stack[idx] = step
      write('replaceState', step)
    },
    back(to) {
      const j = stack.lastIndexOf(to, idx - 1)
      if (idx > 0 && j >= 0) {
        h.go(j - idx)
        return
      }
      this.replace(to)
    },
    onPop(cb) {
      const listener = (e: PopStateEvent) => {
        if (!isEntry(e.state, sessionId)) return
        idx = e.state.idx
        cb(e.state.step)
      }
      window.addEventListener('popstate', listener)
      return () => {
        window.removeEventListener('popstate', listener)
      }
    },
  }
}

/** Embed (task 08) and tests: no browser entries; Back moves an internal stack. */
export function createMemoryStepHistory(): StepHistory & {
  readonly stack: readonly BookingStep[]
  readonly idx: number
} {
  let stack: BookingStep[] = []
  let idx = 0
  const listeners = new Set<(target: BookingStep) => void>()
  return {
    get stack() {
      return stack
    },
    get idx() {
      return idx
    },
    start(step) {
      stack = [step]
      idx = 0
    },
    push(step) {
      stack = [...stack.slice(0, idx + 1), step]
      idx += 1
    },
    replace(step) {
      stack[idx] = step
    },
    back(to) {
      const j = stack.lastIndexOf(to, idx - 1)
      if (j >= 0) idx = j
      else stack[idx] = to
      for (const l of listeners) l(to)
    },
    onPop(cb) {
      listeners.add(cb)
      return () => {
        listeners.delete(cb)
      }
    },
  }
}
